import { Component, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApplicationRoles, EdlCommonModalService, EdlEntriesFormBuilderService, EdlEntriesFormGroup, EdlEntry, EDLGroupType, EdlPortalToken, EdlPortalUserSearchCriteria, Group, PortalRole, PortalService, SessionService } from '@edl-development/edl-common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { StateService } from '@uirouter/core';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { SelectGroupService } from 'src/app/groups/common/services/select-group.service';
import { EdlToggleRoleModalComponent } from 'src/app/modals/edl-toggle-role-modal/edl-toggle-role-modal.component';
import { AccessRoleHelper } from 'src/app/model/helpers/access-role.helper';

@Component({
  selector: 'app-token-admin-form',
  templateUrl: './token-admin-form.component.html',
  styleUrls: ['./token-admin-form.component.scss']
})
export class TokenManagementFormComponent {

  @Input() token: EdlPortalToken;
  @Input() searchCriteria: EdlPortalUserSearchCriteria;

  form: FormGroup;
  userRoles: string[] = ['Admin', 'Editor', 'Viewer'];
  busy = false;

  ApplicationRoles = ApplicationRoles;

  constructor(private fb: FormBuilder,
    private edlCommonModalService: EdlCommonModalService,
    private stateService: StateService,
    private ngbModal: NgbModal,
    private edlEntryFormBuilderService: EdlEntriesFormBuilderService,
    private portalService: PortalService,
    private toastrService: ToastrService,
    private selectGroupService: SelectGroupService,
    private sessionService: SessionService
  ) {}

  ngOnInit() {
    if(this.token == null) {
      this.token = new EdlPortalToken();
    }
    this.buildForm();
    if(!AccessRoleHelper.isEDLAdmin(this.sessionService.user.AccessRoleID)){
      this.form.disable(); // Disable the form for non EDL Admins
    }
  }



  public canUpdate = () => (this.form.valid) && this.form.dirty && !this.busy;
  public addGroupClicked = () => this.filterGroups();
  public addRoleClicked = () => this.filterRoles();

  buildForm() {

    let _appContext = this.token.Context > -1 ? this.token.Context : 0;

    this.form = this.fb.group({
      Description: [this.token.Description],
      Token: [{value: this.token.Token, disabled: true}],
      Context: [_appContext, Validators.required],
      Groups: this.buildGroupsForm(this.token?.Groups ?? []),
      Roles: this.buildRolesForm(this.token?.Roles ?? []),
      ShowIsNT: [this.token?.ShowIsNT],
      ShowUserType: [this.token?.ShowUserType],
      ShowPAETEPBuildings: [this.token?.ShowPAETEPBuildings],
      ShowVolAreas: [this.token?.ShowVolAreas],
      ShowPPID: [this.token?.ShowPPID],
    });
  }

  private buildGroupsForm = (groups: Group[]) => {
    const entries: EdlEntry<Group>[] = groups.map(group => ({ id: group.GroupID, name: group.GroupName, details: group }));
        return this.edlEntryFormBuilderService.buildSelectedFormGroup(entries);
  }


  private buildRolesForm = (roles: PortalRole[]) => {
    const entries: EdlEntry<PortalRole>[] = roles.map(role => ({ id: role.RoleID.toString(), name: role.RoleName, details: role }));
        return this.edlEntryFormBuilderService.buildSelectedFormGroup(entries);
  }

  onSubmit(): void {
    if (this.form.valid) {
      let tokenData= this.packForm();

      firstValueFrom(this.portalService.savePortalToken(tokenData))
      .then((updatedToken: EdlPortalToken) => {
          this.token = updatedToken;

          this.form.get('Token').setValue(updatedToken.Token);
          this.toastrService.success('Token saved successfully');
          this.form.markAsPristine();
      })
      .finally(() => this.busy = false);

    }
    else {
      this.toastrService.error('Please fill in all required fields');
    }
  }

  private packForm() {
    return {
      Description: this.form.value['Description'],
      Token: this.token.Token??"",
      Roles: this.formEntriesToIds(this.form.get('Roles') as EdlEntriesFormGroup<PortalRole>),
      Groups: this.formEntriesToIds(this.form.get('Groups') as EdlEntriesFormGroup<Group>),
      ShowIsNT:  this.form.value['ShowIsNT'],
      ShowUserType:  this.form.value['ShowUserType'],
      ShowPAETEPBuildings:  this.form.value['ShowPAETEPBuildings'],
      ShowComplyBuildings:  this.form.value['ShowComplyBuildings'],
      ShowPPID: this.form.value['ShowPPID'],
      ShowVolAreas: this.form.value['ShowVolAreas'],
      Context: this.form.value['Context'],
    }
  }

  private formEntriesToIds = (form: EdlEntriesFormGroup<any>) => this.edlEntryFormBuilderService.toSelectedEntries(form).map(x => x.id);

  filterRoles() {
    let roles: any[] = [];
    this.searchCriteria.PortalRoles.forEach(roleGroup => {
      let c = roleGroup.Roles
        .map(r => Object.assign({}, r, { IsSelected: !!this.form?.get('Roles')?.get(r.RoleID.toString()) }))
        roles = roles.concat(c );
    });

    roles.sort((a, b) => a.RoleName.localeCompare(b.RoleName));

    this.openRoleModal(roles)
        .then((selectedRoles: PortalRole[]) => {
            const entries: EdlEntry<PortalRole>[] = selectedRoles
                .filter(role => role.IsSelected)
                .map(role => ({ id: role.RoleID.toString(), name: role.RoleName, details: role }));

            this.edlEntryFormBuilderService.updateSelectedFormGroup(this.form.get('Roles') as EdlEntriesFormGroup<PortalRole>, entries);
        }, () => { });
  }

  private openRoleModal(roles: PortalRole[]) {
    let modalRef = this.ngbModal.open(EdlToggleRoleModalComponent, {
        size: 'xl',
        backdrop: 'static'
    });
    modalRef.componentInstance.roles = roles;
    return modalRef.result;
  }


  filterGroups = () => this.selectGroupService.openFilterGroupsModal({
    groupsForm: this.form.get('Groups') as EdlEntriesFormGroup<Group>, 
    groups: this.searchCriteria.PortalGroups,
    filterGroupType: EDLGroupType.GROUP
  });

  public onCancelClicked = () => {
    if (this.form.dirty) {
        this.edlCommonModalService.confirm('Confirmation', 'You have unsaved changes. Are you sure you want to leave this page?')
            .then(() => {
                this.stateService.go('authenticated.administration.token-list');
            },
                () => {
                }).catch(() => { });
    } else {
        this.stateService.go('authenticated.administration.token-list');
    }
}
}